import { useMemo } from 'react';
import {
	Select,
	DatePicker,
	Form,
	Input,
	Button,
	Spin,
	Row,
	Col,
	Card,
	Typography,
	Divider,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FormikProvider } from 'formik';
import { useHistory } from 'react-router-dom';
import CoreBadge from 'src/RnRCommonComponent/CoreBadge/CoreBadge.jsx';
import FormPreview from 'src/new-form-builder/FormPreview';

import './style.scss';

const { Title, Text } = Typography;

const ManageNominatedRewardModal = ({
	isLoading,
	create,
	badgeList = [],
	formik,
	showLoader,
	loading,
	nominatedCount,
	isComplete = false,
	nominationForms = [],
	nominationFormsLoading = false,
}) => {
	const { values, handleSubmit, setFieldValue, errors, submitCount } = formik;
	const history = useHistory();

	const disabledStartDate = (current) => {
		return current < dayjs().startOf('day');
	};

	const disabledFormEdit = useMemo(() => {
		return Boolean(nominatedCount);
	}, [nominatedCount]);

	const selectedBadge =
		badgeList.find((badge) => badge.id === values?.badgeId) || null;

	const selectedNominationForm = useMemo(() => {
		if (!values?.customFormId) return null;
		return (
			nominationForms.find(
				(form) => form?.metadata?.id === values.customFormId
			) || null
		);
	}, [values?.customFormId, nominationForms]);

	const handleNewCustomForm = () => {
		const existingTitles = nominationForms.map(
			(form) => form?.metadata?.title || ''
		);

		let title = 'New Nomination Form';
		if (existingTitles.includes(title)) {
			let counter = 2;
			while (existingTitles.includes(`${title} (${counter})`)) {
				counter++;
			}
			title = `${title} (${counter})`;
		}

		const params = new URLSearchParams({
			prefillConnectionId: 'nomination_form',
			prefillTitle: title,
			prefillDescription:
				'Click to edit this description for a new Custom Nomination form.',
		});

		history.push(`/form-builder?${params.toString()}`);
	};

	return (
		<FormikProvider value={formik} className="manage-nominated-reward">
			{loading || isLoading || showLoader ? (
				<div className="d-flex align-items-center justify-content-center w-100 h-100 edit-modal-loader">
					<Spin />
				</div>
			) : null}

			<Row gutter={24}>
				<Col xs={24} md={10}>
					<div className="preview-panel">
						<Card className="preview-card">
							<div
								className="add-reward-preview"
								style={{
									display: 'flex',
									justifyContent: 'center',
									padding: '0 0 16px',
								}}
							>
								<div style={{ height: '260px', width: '260px' }}>
									<CoreBadge
										captureId={'badge-preview'}
										badgeType={selectedBadge?.badgeType}
										badgeIcon={selectedBadge?.iconInfo?.code}
										label={selectedBadge?.label}
										color={selectedBadge?.color}
										gradientColor={selectedBadge?.gradientColor}
										shape={selectedBadge?.shape}
										badgeRotation={selectedBadge?.badgeRotation}
										customImageSignedUrl={selectedBadge?.customImageSignedUrl}
										labelColor={selectedBadge?.labelColor}
										customIconSignedUrl={selectedBadge?.customIconSignedUrl}
									/>
								</div>
							</div>

							{selectedBadge && (
								<div style={{ textAlign: 'center', marginTop: '16px' }}>
									<Title
										level={5}
										style={{ margin: '0 0 8px 0', color: '#0f172a' }}
									>
										{selectedBadge?.name || 'Recognition Name'}
									</Title>
									<Text
										style={{
											fontSize: '14px',
											color: '#475569',
											display: 'block',
											marginBottom: '16px',
										}}
									>
										{selectedBadge?.description || 'Recognition description'}
									</Text>

									<div className="badge-details-list">
										<div className="detail-row">
											<span className="detail-label">Who Can Nominate:</span>
											<span className="detail-value">
												{selectedBadge?.isAssignmentRestricted
													? 'Managers Only'
													: 'Anyone'}
											</span>
										</div>
										<div className="detail-row">
											<span className="detail-label">Self Nominations:</span>
											<span className="detail-value">
												{selectedBadge?.allowSelfNomination ? 'Yes' : 'No'}
											</span>
										</div>
										<div className="detail-row">
											<span className="detail-label">Points:</span>
											<span className="detail-value">
												{selectedBadge?.points ?? 0}
											</span>
										</div>
										<div className="detail-row">
											<span className="detail-label">Pre-Approved:</span>
											<span className="detail-value">
												{selectedBadge?.isPreApproved ? 'Yes' : 'No'}
											</span>
										</div>
										<div className="detail-row">
											<span className="detail-label">Snappy Reward:</span>
											<span className="detail-value">
												{selectedBadge?.snappyGrant
													? `${selectedBadge?.snappyGrant?.name} - $${selectedBadge?.snappyGrant?.costInCurrency}`
													: 'None'}
											</span>
										</div>
										<div className="detail-row">
											<span className="detail-label">Bonus Reward:</span>
											<span className="detail-value">
												{selectedBadge?.rewardBonus
													? `$${selectedBadge.rewardBonus}`
													: 'None'}
											</span>
										</div>
										{selectedBadge?.certificateConfig?.name && (
											<div className="detail-row">
												<span className="detail-label">Certificate:</span>
												<span className="detail-value">
													{selectedBadge.certificateConfig.name}
												</span>
											</div>
										)}
									</div>
								</div>
							)}
						</Card>
					</div>
				</Col>

				<Col xs={24} md={14}>
					<Form layout="vertical">
						<Title level={4} style={{ color: '#0f172a', marginBottom: '24px' }}>
							Nomination Period Setup
						</Title>

						<Form.Item
							label="Choose Reward"
							required
							validateStatus={submitCount > 0 && errors?.badgeId ? 'error' : ''}
							help={submitCount > 0 && errors?.badgeId}
						>
							<Select
								className="custom-input"
								loading={isLoading}
								placeholder="Select Nomination Award"
								value={values?.badgeId}
								onChange={(event) => {
									setFieldValue('badgeId', event);
								}}
								disabled={disabledFormEdit}
							>
								<Select.Option disabled value="">
									Select Nomination Award
								</Select.Option>
								{badgeList?.map(({ name, id }) => (
									<Select.Option key={id} value={id}>
										{name}
									</Select.Option>
								))}
							</Select>
						</Form.Item>

						<Form.Item
							label="Nomination Period"
							required
							validateStatus={
								submitCount > 0 && (errors?.startDate || errors?.endDate)
									? 'error'
									: ''
							}
							help={submitCount > 0 && (errors?.startDate || errors?.endDate)}
						>
							<Row gutter={16} style={{ marginLeft: 0, marginRight: 0 }}>
								<Col span={12} style={{ paddingLeft: 0 }}>
									<DatePicker
										value={values.startDate ? dayjs(values.startDate) : ''}
										format={'MM/DD/YYYY'}
										allowClear={false}
										disabled={Boolean(nominatedCount)}
										disabledDate={disabledStartDate}
										placeholder="Select Start Date"
										onChange={(event) => {
											let startDate = dayjs(event);
											setFieldValue('startDate', startDate.toDate());
										}}
										style={{ width: '100%' }}
									/>
								</Col>
								<Col span={12} style={{ paddingRight: 0 }}>
									<DatePicker
										value={values.endDate ? dayjs(values.endDate) : ''}
										format={'MM/DD/YYYY'}
										allowClear={false}
										disabled={isComplete}
										disabledDate={disabledStartDate}
										placeholder="Select End Date"
										onChange={(event) => {
											let endDate = dayjs(event);
											setFieldValue('endDate', endDate.toDate());
										}}
										style={{ width: '100%' }}
									/>
								</Col>
							</Row>
						</Form.Item>

						<Row gutter={16}>
							<Col span={12}>
								<Form.Item
									label="How Many Awardees"
									required
									validateStatus={
										submitCount > 0 &&
										errors?.nominationCriteria?.nominator?.awardedCount
											? 'error'
											: ''
									}
									help={
										submitCount > 0 &&
										errors?.nominationCriteria?.nominator?.awardedCount
									}
								>
									<Input
										type="number"
										min={1}
										max={999}
										value={values.nominationCriteria.nominator.awardedCount}
										placeholder="How Many Awardees..."
										onChange={(event) => {
											setFieldValue(
												'nominationCriteria.nominator.awardedCount',
												event.target.value
											);
										}}
										disabled={isComplete}
										className="custom-input"
									/>
								</Form.Item>
							</Col>
							<Col span={12}>
								<Form.Item
									label="Custom Nomination Form"
									style={{ width: '100%' }}
								>
									<div
										style={{
											display: 'flex',
											gap: '8px',
											alignItems: 'center',
											width: '100%',
										}}
									>
										<Select
											className="custom-input"
											loading={nominationFormsLoading}
											placeholder="Select Form (optional)"
											value={values?.customFormId || undefined}
											onChange={(value) =>
												setFieldValue('customFormId', value || null)
											}
											allowClear
											onClear={() => setFieldValue('customFormId', null)}
											style={{ flex: 1, minWidth: 0 }}
										>
											{nominationForms.map((form) => (
												<Select.Option
													key={form?.metadata?.id}
													value={form?.metadata?.id}
												>
													{form?.metadata?.title || 'Untitled Custom Form'}
												</Select.Option>
											))}
										</Select>
										<Button
											type="dashed"
											icon={<PlusOutlined />}
											onClick={handleNewCustomForm}
											title="New Custom Form"
										/>
									</div>
								</Form.Item>
							</Col>
						</Row>

						{selectedNominationForm && (
							<div className="nomination-form-preview">
								<Card
									title={
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '8px',
											}}
										>
											<span style={{ fontWeight: 900 }}>
												Nomination Form Preview
											</span>
											<Button
												type="link"
												style={{
													fontSize: '12px',
													fontWeight: 400,
												}}
												onClick={() =>
													history.push(
														`/form-builder?customFormId=${values?.customFormId}`
													)
												}
											>
												Edit Form
											</Button>
										</div>
									}
									size="small"
									style={{ marginBottom: '16px' }}
								>
									<FormPreview
										elements={selectedNominationForm.elements || []}
										formMetadata={selectedNominationForm.metadata || {}}
										formType="custom"
										disabled
										isPreview={false}
									/>
								</Card>
							</div>
						)}

						<div className="submit-section">
							{showLoader ? (
								<Spin />
							) : (
								<Button
									disabled={isComplete}
									onClick={handleSubmit}
									type="primary"
									size="large"
								>
									{create ? 'Create' : 'Update'}
								</Button>
							)}
						</div>
					</Form>
				</Col>
			</Row>
		</FormikProvider>
	);
};

export default ManageNominatedRewardModal;
